import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Briefcase, Building2, CalendarDays, MessageCircle, PhoneCall, UserCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/recruiting/StatusBadge";
import { PipelineStepper } from "@/components/recruiting/PipelineStepper";
import { ActivityTimeline } from "@/components/recruiting/ActivityTimeline";
import { api } from "@/lib/api";
import { formatDate, waLink } from "@/lib/constants";

export default function MemberApplicationDetail() {
  const { appId } = useParams();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/applications/${appId}`).then((res) => setApp(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, [appId]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-2/3 rounded-lg" />
        <Skeleton className="h-40 rounded-xl" />
      </div>
    );
  }

  if (!app) return <p className="text-muted-foreground">Application not found.</p>;

  const history = [...(app.status_history || [])].reverse();
  const contactAttempted = app.status === "Contact Attempted";

  return (
    <div className="space-y-6">
      <Link to="/member" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="application-back-link">
        <ArrowLeft className="h-4 w-4" /> My applications
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <span className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Briefcase className="h-6 w-6" />
        </span>
        <div className="flex-1">
          <h1 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight" data-testid="application-job-title">{app.job?.title}</h1>
          <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{app.job?.hiring_brand}</span>
            <span>{app.job?.location}</span>
            <span className="font-mono text-xs">{app.code}</span>
          </p>
        </div>
        <StatusBadge status={app.status} className="text-sm px-3 py-1" />
      </div>

      {contactAttempted && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5 flex items-start gap-3" data-testid="contact-attempted-banner">
          <PhoneCall className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">We tried contacting you.</p>
            <p className="text-xs text-amber-700 mt-0.5">Our recruiter attempted to reach you on your mobile number. Please stay available or message them on WhatsApp.</p>
          </div>
        </div>
      )}

      <Card className="rounded-xl border-border shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-heading">Application progress</CardTitle>
        </CardHeader>
        <CardContent>
          <PipelineStepper status={app.status} />
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <div className="space-y-6">
          {app.recruiter ? (
            <Card className="rounded-xl border-primary/25 bg-primary/5 shadow-none" data-testid="assigned-recruiter-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <UserCircle className="h-4 w-4 text-primary" /> Your recruiter
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <span className="h-11 w-11 rounded-full bg-white border border-primary/20 text-primary flex items-center justify-center font-semibold">
                    {(app.recruiter.name || "?").charAt(0).toUpperCase()}
                  </span>
                  <div className="flex-1">
                    <p className="font-medium text-sm" data-testid="recruiter-name">{app.recruiter.name}</p>
                    <p className="text-xs text-muted-foreground">Handling your application personally</p>
                  </div>
                </div>
                {app.recruiter.whatsapp_number && (
                  <a href={waLink(app.recruiter.whatsapp_number)} target="_blank" rel="noreferrer" className="block mt-4" data-testid="recruiter-whatsapp-link">
                    <Button className="w-full rounded-lg bg-emerald-600 hover:bg-emerald-700">
                      <MessageCircle className="h-4 w-4 mr-2" /> Message on WhatsApp
                    </Button>
                  </a>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="rounded-xl border-border shadow-none" data-testid="no-recruiter-card">
              <CardContent className="p-5">
                <p className="text-sm font-medium">Recruiter assignment in progress</p>
                <p className="text-xs text-muted-foreground mt-1">A recruiter will pick up your application shortly and contact you.</p>
              </CardContent>
            </Card>
          )}

          {app.interview && (
            <Card className="rounded-xl border-violet-200 bg-violet-50/50 shadow-none" data-testid="interview-details-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-violet-600" /> Interview details
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1.5">
                <p><span className="text-muted-foreground">Date:</span> <span className="font-medium">{formatDate(app.interview.date)}</span></p>
                {app.interview.time && <p><span className="text-muted-foreground">Time:</span> <span className="font-medium">{app.interview.time}</span></p>}
                {app.interview.mode && <p><span className="text-muted-foreground">Mode:</span> <span className="font-medium">{app.interview.mode}</span></p>}
                {app.interview.location_or_link && <p className="break-all"><span className="text-muted-foreground">Where:</span> <span className="font-medium">{app.interview.location_or_link}</span></p>}
              </CardContent>
            </Card>
          )}
        </div>

        <Card className="rounded-xl border-border shadow-none">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">Application timeline</CardTitle>
          </CardHeader>
          <CardContent>
            <ActivityTimeline items={history} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
